import { useState, useEffect, useRef, useCallback } from 'react';
import { locationsApi, fieldNotesApi, activitiesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, MapPin, Trash2, History, FileText, Activity, Droplet, SprayCan, Scissors, Undo, PenTool, Upload, X, Image } from 'lucide-react';
import Map, { Marker, Source, Layer, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

// Map styles
const MAP_STYLES = {
  satellite: {
    version: 8,
    sources: {
      'esri-satellite': {
        type: 'raster',
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
        tileSize: 256,
        attribution: '&copy; Esri'
      }
    },
    layers: [{ id: 'esri-satellite', type: 'raster', source: 'esri-satellite', minzoom: 0, maxzoom: 19 }]
  },
  street: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
};

const FARM_CENTER = { longitude: 153.137, latitude: -27.943, zoom: 15 };

const NOTE_TYPES = [
  { value: 'treatment', label: 'Treatment', icon: SprayCan, color: 'bg-orange-100 text-orange-800' },
  { value: 'observation', label: 'Observation', icon: FileText, color: 'bg-blue-100 text-blue-800' },
  { value: 'quality_update', label: 'Quality Update', icon: Scissors, color: 'bg-green-100 text-green-800' },
  { value: 'fertilizing', label: 'Fertilizing', icon: Droplet, color: 'bg-amber-100 text-amber-800' },
];

// Calculate area of polygon using Shoelace formula
function calculatePolygonArea(coords) {
  if (coords.length < 3) return { sqm: 0, hectares: 0 };
  
  // Convert to radians for spherical calculation
  const toRad = (deg) => deg * Math.PI / 180;
  const R = 6371000; // Earth radius in meters
  
  let area = 0;
  const n = coords.length;
  
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const lat1 = toRad(coords[i][1]);
    const lat2 = toRad(coords[j][1]);
    const lon1 = toRad(coords[i][0]);
    const lon2 = toRad(coords[j][0]);
    
    area += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  
  area = Math.abs(area * R * R / 2);
  
  return {
    sqm: Math.round(area),
    hectares: (area / 10000).toFixed(2)
  };
}

export function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [notePhotos, setNotePhotos] = useState([]);
  const [mapStyle, setMapStyle] = useState('satellite');
  const [activeTab, setActiveTab] = useState('map');
  const [fieldNotes, setFieldNotes] = useState([]);
  const [fieldActivities, setFieldActivities] = useState([]);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [boundaryPoints, setBoundaryPoints] = useState([]);
  const [fieldName, setFieldName] = useState('');
  const [fieldDescription, setFieldDescription] = useState('');
  
  const mapRef = useRef(null);

  const [viewState, setViewState] = useState({
    longitude: FARM_CENTER.longitude,
    latitude: FARM_CENTER.latitude,
    zoom: FARM_CENTER.zoom,
  });

  const loadLocations = useCallback(async () => {
    try {
      const res = await locationsApi.getAll();
      setLocations(res.data);
    } catch (err) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    if (selectedLocation) {
      loadFieldHistory(selectedLocation.id);
    }
  }, [selectedLocation]);

  const loadFieldHistory = async (locationId) => {
    try {
      const [notesRes, activitiesRes] = await Promise.all([
        fieldNotesApi.getAll(locationId),
        activitiesApi.getAll({ location_id: locationId }),
      ]);
      setFieldNotes(notesRes.data);
      setFieldActivities(activitiesRes.data);
    } catch (err) {
      // Field history is supplementary, don't block UI
    }
  };

  const handleMapClick = useCallback((e) => {
    if (isDrawing) {
      const point = [e.lngLat.lng, e.lngLat.lat];
      setBoundaryPoints(prev => [...prev, point]);
    }
  }, [isDrawing, setBoundaryPoints]);

  const startDrawing = () => {
    setIsDrawing(true);
    setBoundaryPoints([]);
    setFieldName('');
    setFieldDescription('');
    setDialogOpen(false);
    toast.info('Click on the map to mark the corners of your paddock boundary');
  };

  const undoLastPoint = () => {
    setBoundaryPoints(prev => prev.slice(0, -1));
  };

  const cancelDrawing = () => {
    setIsDrawing(false);
    setBoundaryPoints([]);
  };

  const finishDrawing = () => {
    if (boundaryPoints.length < 3) {
      toast.error('Please mark at least 3 points to define the paddock boundary');
      return;
    }
    setIsDrawing(false);
    setDialogOpen(true);
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (boundaryPoints.length < 3) {
      toast.error('Please draw the paddock boundary first');
      return;
    }
    
    const area = calculatePolygonArea(boundaryPoints);
    
    try {
      await locationsApi.create({
        name: fieldName,
        description: fieldDescription || null,
        coordinates: boundaryPoints,
        area_hectares: parseFloat(area.hectares),
      });
      toast.success(`Field "${fieldName}" added - ${area.hectares} hectares`);
      setDialogOpen(false);
      setBoundaryPoints([]);
      setFieldName('');
      setFieldDescription('');
      loadLocations();
    } catch (err) {
      toast.error('Failed to add field');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      const res = await fieldNotesApi.create({
        location_id: selectedLocation.id,
        date: form.get('date'),
        note_type: form.get('note_type'),
        content: form.get('content'),
      });
      // Upload photos
      if (notePhotos.length > 0) {
        const noteId = res.data.id;
        for (const file of notePhotos) {
          const fd = new FormData();
          fd.append('file', file);
          try { await fieldNotesApi.uploadPhoto(noteId, fd); } catch (err) { console.error('Note photo upload failed:', err); }
        }
      }
      toast.success('Note added');
      setNoteDialogOpen(false);
      setNotePhotos([]);
      loadFieldHistory(selectedLocation.id);
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this field?')) return;
    try {
      await locationsApi.delete(id);
      toast.success('Field deleted');
      loadLocations();
      if (selectedLocation?.id === id) {
        setSelectedLocation(null);
        setFieldNotes([]);
        setFieldActivities([]);
      }
    } catch (err) {
      toast.error('Failed to delete field');
    }
  };

  const selectField = (loc) => {
    setSelectedLocation(loc);
    // Center on field and zoom to show boundary
    if (loc.coordinates?.length > 0) {
      const lngs = loc.coordinates.map(c => c[0]);
      const lats = loc.coordinates.map(c => c[1]);
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      setViewState({
        longitude: centerLng,
        latitude: centerLat,
        zoom: 16,
      });
    }
  };

  // Combine notes and activities into a timeline
  const getFieldTimeline = () => {
    const timeline = [
      ...fieldNotes.map(n => ({ ...n, type: 'note', sortDate: n.date })),
      ...fieldActivities.map(a => ({ ...a, type: 'activity', sortDate: a.date })),
    ];
    return timeline.sort((a, b) => b.sortDate.localeCompare(a.sortDate));
  };

  const calculatedArea = calculatePolygonArea(boundaryPoints);

  // Polygon layers for existing fields (subtle green)
  const fieldFillLayer = {
    id: 'field-fill',
    type: 'fill',
    paint: { 'fill-color': '#2D6A4F', 'fill-opacity': 0.2 },
  };
  
  const fieldOutlineLayer = {
    id: 'field-outline',
    type: 'line',
    paint: { 'line-color': '#2D6A4F', 'line-width': 2 },
  };

  // Selected field — bright green matching reference
  const selectedFieldFillLayer = {
    id: 'selected-field-fill',
    type: 'fill',
    paint: { 'fill-color': '#40B763', 'fill-opacity': 0.3 },
  };

  const selectedFieldOutlineLayer = {
    id: 'selected-field-outline',
    type: 'line',
    paint: { 'line-color': '#2E8B47', 'line-width': 3 },
  };

  // Drawing polygon layer
  const drawingFillLayer = {
    id: 'drawing-fill',
    type: 'fill',
    paint: { 'fill-color': '#3B82F6', 'fill-opacity': 0.3 },
  };

  const drawingOutlineLayer = {
    id: 'drawing-outline',
    type: 'line',
    paint: { 'line-color': '#3B82F6', 'line-width': 2, 'line-dasharray': [2, 2] },
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded-md" data-testid="locations-loading" />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locations-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Fields & Locations</h1>
          <p className="text-muted-foreground">Manage your fields, paddocks, and treatment history</p>
        </div>
        {!isDrawing && (
          <Button className="btn-primary" onClick={startDrawing} data-testid="add-location-btn">
            <PenTool className="h-4 w-4 mr-2" /> Draw New Field
          </Button>
        )}
      </div>

      {/* Drawing Mode Banner */}
      {isDrawing && (
        <Card className="border-blue-500 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <PenTool className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-900">Drawing Paddock Boundary</p>
                  <p className="text-sm text-blue-700">
                    Click on the map to mark each corner. Points: <strong>{boundaryPoints.length}</strong>
                    {boundaryPoints.length >= 3 && (
                      <span className="ml-2">
                        | Area: <strong>{calculatedArea.hectares} ha</strong> ({calculatedArea.sqm.toLocaleString()} m²)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {boundaryPoints.length > 0 && (
                  <Button variant="outline" size="sm" onClick={undoLastPoint}>
                    <Undo className="h-4 w-4 mr-1" /> Undo
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={cancelDrawing}>
                  Cancel
                </Button>
                {boundaryPoints.length >= 3 && (
                  <Button size="sm" onClick={finishDrawing} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-1" /> Finish & Save
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Fields List */}
        <Card className="lg:col-span-1" data-testid="locations-list">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Fields ({locations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {locations.length === 0 ? (
              <p className="text-muted-foreground text-center py-8 px-4">No fields added yet</p>
            ) : (
              <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
                {locations.map((loc) => (
                  <div
                    key={loc.id}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedLocation?.id === loc.id ? 'bg-primary/10' : 'hover:bg-secondary/50'
                    }`}
                    onClick={() => selectField(loc)}
                    data-testid={`location-item-${loc.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{loc.name}</p>
                        {loc.area_hectares && (
                          <p className="text-sm text-muted-foreground">{loc.area_hectares} ha ({Math.round(loc.area_hectares * 10000).toLocaleString()} m²)</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLocation(loc.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content Area with Tabs */}
        <Card className="lg:col-span-3 overflow-hidden" data-testid="locations-main">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="px-6 pt-4 border-b">
              <TabsList>
                <TabsTrigger value="map" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Map View
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center gap-2" disabled={!selectedLocation}>
                  <History className="h-4 w-4" /> Field History
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Map Tab */}
            <TabsContent value="map" className="m-0">
              <div className="relative">
                <div className="absolute top-4 left-4 z-20 flex gap-2">
                  <Button
                    size="sm"
                    variant={mapStyle === 'satellite' ? 'default' : 'outline'}
                    onClick={() => setMapStyle('satellite')}
                    className="text-xs shadow-md"
                  >
                    Satellite
                  </Button>
                  <Button
                    size="sm"
                    variant={mapStyle === 'street' ? 'default' : 'outline'}
                    onClick={() => setMapStyle('street')}
                    className="text-xs shadow-md"
                  >
                    Street
                  </Button>
                </div>
                <div className="h-[500px]">
                  <Map
                    ref={mapRef}
                    {...viewState}
                    onMove={(e) => setViewState(e.viewState)}
                    onClick={handleMapClick}
                    mapStyle={MAP_STYLES[mapStyle]}
                    style={{ width: '100%', height: '100%' }}
                    cursor={isDrawing ? 'crosshair' : 'grab'}
                  >
                    <NavigationControl position="top-right" />

                    {/* Existing field polygons */}
                    {locations.map((loc) => (
                      loc.coordinates?.length >= 3 && loc.id !== selectedLocation?.id && (
                        <Source
                          key={loc.id}
                          id={`field-${loc.id}`}
                          type="geojson"
                          data={{
                            type: 'Feature',
                            geometry: {
                              type: 'Polygon',
                              coordinates: [[...loc.coordinates, loc.coordinates[0]]],
                            },
                          }}
                        >
                          <Layer {...fieldFillLayer} id={`fill-${loc.id}`} />
                          <Layer {...fieldOutlineLayer} id={`outline-${loc.id}`} />
                        </Source>
                      )
                    ))}

                    {/* Selected field polygon */}
                    {selectedLocation?.coordinates?.length >= 3 && (
                      <Source
                        id="selected-field"
                        type="geojson"
                        data={{
                          type: 'Feature',
                          geometry: {
                            type: 'Polygon',
                            coordinates: [[...selectedLocation.coordinates, selectedLocation.coordinates[0]]],
                          },
                        }}
                      >
                        <Layer {...selectedFieldFillLayer} />
                        <Layer {...selectedFieldOutlineLayer} />
                      </Source>
                    )}

                    {/* Selected field boundary point markers */}
                    {!isDrawing && selectedLocation?.coordinates?.length >= 3 && selectedLocation.coordinates.map((point, idx) => (
                      <Marker key={`sel-${idx}`} longitude={point[0]} latitude={point[1]}>
                        <div className="w-7 h-7 rounded-full bg-[#2E8B47] border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold">
                          {idx + 1}
                        </div>
                      </Marker>
                    ))}

                    {/* Drawing polygon */}
                    {isDrawing && boundaryPoints.length >= 2 && (
                      <Source
                        id="drawing-polygon"
                        type="geojson"
                        data={{
                          type: 'Feature',
                          geometry: {
                            type: boundaryPoints.length >= 3 ? 'Polygon' : 'LineString',
                            coordinates: boundaryPoints.length >= 3 
                              ? [[...boundaryPoints, boundaryPoints[0]]] 
                              : boundaryPoints,
                          },
                        }}
                      >
                        {boundaryPoints.length >= 3 && <Layer {...drawingFillLayer} />}
                        <Layer {...drawingOutlineLayer} />
                      </Source>
                    )}

                    {/* Drawing markers */}
                    {isDrawing && boundaryPoints.map((point, idx) => (
                      <Marker key={`bnd-${point[0]}-${point[1]}`} longitude={point[0]} latitude={point[1]}>
                        <div className="w-6 h-6 rounded-full bg-blue-600 border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold">
                          {idx + 1}
                        </div>
                      </Marker>
                    ))}

                    {/* Field center markers (only for fields with < 3 points) */}
                    {!isDrawing && locations.map((loc) => (
                      loc.coordinates?.length > 0 && loc.coordinates.length < 3 && (
                        <Marker 
                          key={loc.id} 
                          longitude={loc.coordinates[0][0]} 
                          latitude={loc.coordinates[0][1]}
                          onClick={(e) => {
                            e.originalEvent.stopPropagation();
                            selectField(loc);
                          }}
                        >
                          <div 
                            className={`flex items-center justify-center w-8 h-8 rounded-full border-2 shadow-lg cursor-pointer transition-transform hover:scale-110 ${
                              selectedLocation?.id === loc.id 
                                ? 'bg-accent border-white' 
                                : 'bg-primary border-white'
                            }`}
                          >
                            <MapPin className="h-4 w-4 text-white" />
                          </div>
                        </Marker>
                      )
                    ))}
                  </Map>
                </div>
              </div>

              {/* Selected Field Info */}
              {selectedLocation && !isDrawing && (
                <div className="p-4 border-t bg-secondary/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading font-semibold text-lg">{selectedLocation.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {selectedLocation.area_hectares && `${selectedLocation.area_hectares} ha (${Math.round(selectedLocation.area_hectares * 10000).toLocaleString()} m²)`}
                        {selectedLocation.coordinates?.length >= 3 && ` • ${selectedLocation.coordinates.length} boundary points`}
                        {selectedLocation.description && ` • ${selectedLocation.description}`}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => setActiveTab('history')}>
                      <History className="h-4 w-4 mr-1" /> View History
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Field History Tab */}
            <TabsContent value="history" className="m-0">
              {selectedLocation ? (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="font-heading font-semibold text-xl">{selectedLocation.name} - History</h3>
                      <p className="text-sm text-muted-foreground">
                        Treatment records, observations, and activity log
                      </p>
                    </div>
                    <Button onClick={() => setNoteDialogOpen(true)} data-testid="add-note-btn">
                      <Plus className="h-4 w-4 mr-1" /> Add Note
                    </Button>
                  </div>

                  {/* Timeline */}
                  <div className="space-y-4">
                    {getFieldTimeline().length === 0 ? (
                      <div className="text-center py-12">
                        <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-lg font-medium">No history yet</p>
                        <p className="text-muted-foreground">Add notes or record activities for this field</p>
                      </div>
                    ) : (
                      getFieldTimeline().map((item, idx) => {
                        if (item.type === 'note') {
                          const noteType = NOTE_TYPES.find(t => t.value === item.note_type) || NOTE_TYPES[0];
                          const Icon = noteType.icon;
                          return (
                            <div key={`note-${item.id}`} className="flex gap-4 p-4 bg-card border rounded-lg">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${noteType.color}`}>
                                <Icon className="h-5 w-5" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={noteType.color}>{noteType.label}</Badge>
                                  <span className="text-sm text-muted-foreground">{item.date}</span>
                                </div>
                                <p className="text-foreground">{item.content}</p>
                                {item.photos?.length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {item.photos.map((photo, pIdx) => (
                                      <a key={`field-photo-${photo}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo}`} target="_blank" rel="noopener noreferrer">
                                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo}`} alt="" className="w-16 h-16 rounded-md object-cover border hover:opacity-80 transition-opacity" />
                                      </a>
                                    ))}
                                  </div>
                                )}
                                {item.created_by && (
                                  <p className="text-xs text-muted-foreground mt-1">by {item.created_by}</p>
                                )}
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div key={`activity-${item.id}`} className="flex gap-4 p-4 bg-card border rounded-lg">
                              <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: item.activity_type_color || '#1B4332' }}
                              >
                                <Activity className="h-5 w-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge style={{ backgroundColor: item.activity_type_color, color: 'white' }}>
                                    {item.activity_type_name}
                                  </Badge>
                                  <span className="text-sm text-muted-foreground">{item.date}</span>
                                </div>
                                {item.area_sqm && (
                                  <p className="text-sm">Area: {(item.area_sqm / 10000).toFixed(2)} ha</p>
                                )}
                                {item.machinery_name && (
                                  <p className="text-sm">Equipment: {item.machinery_name}</p>
                                )}
                                {item.notes && <p className="text-foreground mt-1">{item.notes}</p>}
                                {item.weather && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Weather: {item.weather.temp}°C, {item.weather.conditions}
                                  </p>
                                )}
                                {item.created_by && (
                                  <p className="text-xs text-muted-foreground mt-1">by {item.created_by}</p>
                                )}
                              </div>
                            </div>
                          );
                        }
                      })
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">Select a field</p>
                  <p className="text-muted-foreground">Choose a field from the list to view its history</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Save Field Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Save New Field</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLocation} className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Boundary:</strong> {boundaryPoints.length} points
              </p>
              <p className="text-lg font-bold text-blue-900">
                Area: {calculatedArea.hectares} hectares ({calculatedArea.sqm.toLocaleString()} m²)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Field Name *</Label>
              <Input 
                id="name" 
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                placeholder="e.g., P1, North Paddock" 
                required 
                data-testid="location-name-input" 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea 
                id="description" 
                value={fieldDescription}
                onChange={(e) => setFieldDescription(e.target.value)}
                placeholder="Notes about this field..." 
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => { setDialogOpen(false); setBoundaryPoints([]); }} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="location-submit-btn">
                Save Field
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Note - {selectedLocation?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddNote} className="space-y-4">
            <div className="space-y-2">
              <Label>Note Type *</Label>
              <Select name="note_type" required defaultValue="observation">
                <SelectTrigger data-testid="note-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2">
                        <t.icon className="h-4 w-4" />
                        {t.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input name="date" type="date" required defaultValue={new Date().toISOString().split('T')[0]} />
            </div>
            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea 
                name="content" 
                required 
                placeholder="Describe the treatment, observation, or quality update..."
                className="min-h-[100px]"
              />
            </div>
            <div className="space-y-2">
              <Label>Photos</Label>
              <div className="border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => document.getElementById('note-photo-input').click()}>
                <input id="note-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => setNotePhotos(prev => [...prev, ...Array.from(e.target.files)])} data-testid="note-photo-input" />
                <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
                <p className="text-xs text-muted-foreground">Click to add photos</p>
              </div>
              {notePhotos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {notePhotos.map((file, idx) => (
                    <div key={`note-photo-${file.name}-${idx}`} className="relative group">
                      <img src={URL.createObjectURL(file)} alt="" className="w-14 h-14 rounded object-cover border" />
                      <button type="button" className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setNotePhotos(prev => prev.filter((_, i) => i !== idx))}>
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setNoteDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 btn-primary">Add Note</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
